/**
 * AS SEIS FASES DA JORNADA DE NEGÓCIOS (CERES-UX-001).
 *
 *   Entrada → Diagnóstico → Solução → Proposta → Negociação → Fechamento
 *
 * AS FASES NÃO SÃO ETAPAS NOVAS. Cada tenant tem o pipeline que configurou —
 * o padrão da BugKillers tem oito etapas, um tenant novo tem cinco —, e nenhuma
 * etapa é renomeada, movida ou criada por causa desta jornada. A fase é um
 * AGRUPAMENTO das etapas reais:
 *
 * 1. se a etapa tem `processPhase` configurado (Pipelines › etapa), vale ele;
 * 2. senão, ganha/perdida é sempre Fechamento;
 * 3. senão, o NOME ou a CHAVE da etapa indica a fase (lista de termos abaixo);
 * 4. senão, a POSIÇÃO entre as etapas abertas distribui pelas cinco primeiras.
 *
 * A regra é a mesma na API e na tela (este arquivo é o contrato das duas), e é
 * determinística: a mesma configuração sempre produz o mesmo agrupamento.
 */

export const FASES_DO_NEGOCIO = [
  'ENTRADA',
  'DIAGNOSTICO',
  'SOLUCAO',
  'PROPOSTA',
  'NEGOCIACAO',
  'FECHAMENTO',
] as const;

export type FaseDoNegocio = (typeof FASES_DO_NEGOCIO)[number];

export interface EtapaParaFase {
  id: string;
  key: string;
  name: string;
  sortOrder: number;
  stageType: 'OPEN' | 'WON' | 'LOST';
  processPhase?: string | null;
}

/** Termos que, no nome ou na chave da etapa, indicam a fase. Ordem importa. */
const TERMOS_DA_FASE: ReadonlyArray<[FaseDoNegocio, RegExp]> = [
  ['NEGOCIACAO', /negocia|aprova|contrata|jur[ií]dic|fechament/],
  ['PROPOSTA', /propost|or[cç]ament|cota[cç]/],
  ['SOLUCAO', /solu[cç]|levantament|estimativ|demonstra|piloto|desenh|escopo|t[eé]cnic/],
  ['DIAGNOSTICO', /diagn[oó]st|qualifica|discovery|descoberta|necessidade/],
  ['ENTRADA', /entrada|prospec|contato|mapead|novo|lead|inicial/],
];

function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export function ehFaseDoNegocio(valor: unknown): valor is FaseDoNegocio {
  return typeof valor === 'string' && (FASES_DO_NEGOCIO as readonly string[]).includes(valor);
}

/** A fase de UMA etapa, considerando as demais do mesmo pipeline. */
export function faseDaEtapa(etapa: EtapaParaFase, todas: readonly EtapaParaFase[]): FaseDoNegocio {
  if (ehFaseDoNegocio(etapa.processPhase)) return etapa.processPhase;
  if (etapa.stageType !== 'OPEN') return 'FECHAMENTO';

  const texto = normalizar(`${etapa.name} ${etapa.key}`);
  const porTermo = TERMOS_DA_FASE.find(([, termo]) => termo.test(texto));
  if (porTermo !== undefined) return porTermo[0];

  // Pela posição entre as ABERTAS: distribui pelas cinco primeiras fases.
  const abertas = [...todas].filter((e) => e.stageType === 'OPEN').sort((a, b) => a.sortOrder - b.sortOrder);
  const indice = Math.max(0, abertas.findIndex((e) => e.id === etapa.id));
  if (abertas.length <= 1) return 'ENTRADA';
  const fatia = Math.min(4, Math.floor((indice / (abertas.length - 1)) * 4.999));
  return FASES_DO_NEGOCIO[fatia] ?? 'ENTRADA';
}

/** Mapa etapa → fase de um pipeline inteiro. */
export function fasesDoPipeline(etapas: readonly EtapaParaFase[]): Map<string, FaseDoNegocio> {
  return new Map(etapas.map((e) => [e.id, faseDaEtapa(e, etapas)]));
}
